/**
 * Formatacao de data e hora para EXIBICAO — DESIGN_SYSTEM §5.7 (casos 2 e 17) e §6.4.
 *
 * Por que isto existe e por que fica aqui:
 *
 * O contrato entrega carimbo de tempo (`promotionEndsAt`, `checkedAt`, `Availability.checkedAt`,
 * `lastPurchasedAt`), e a tabela do §5.7 manda exibir `Válido até 30/09` e `conferido às 14:32`.
 * Sem uma camada de formatacao, o componente imprime `2026-09-30T23:59:00-03:00` na tela do
 * cliente — que foi exatamente o que apareceu no catalogo antes deste arquivo existir.
 *
 * **Nao e regra de negocio** (§51). Nada aqui decide se a promocao vale, se o dado esta velho ou
 * se a filial degrada: isso e `@farmacia/domain`. Aqui so se decide como falar a data que ja
 * chegou decidida.
 *
 * Fuso: o do runtime. O produto e de instancia dedicada por cliente e roda no fuso da farmacia;
 * fixar `America/Fortaleza` aqui seria escrever Solonopole no codigo do produto (ADR-0025).
 */

/**
 * `2026-09-30T23:59:00-03:00` -> `30/09`.
 *
 * Entrada que nao e carimbo valido volta **inalterada**, de proposito: um ERP que manda
 * `"30/09"` ou `""` deve produzir `30/09` ou nada na tela, nunca `Invalid Date`. Data quebrada
 * na tela de um cliente e pior que data ausente, e o lugar de reprovar dado ruim e o contrato
 * (Zod, `@farmacia/contracts`), nao a linha do preco.
 */
export function formatarDiaEMes(carimbo: string): string {
  const data = interpretar(carimbo);
  if (data === null) return carimbo;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(data);
}

/** `2026-09-02T14:32:00-03:00` -> `14:32`. Mesma tolerancia de `formatarDiaEMes`. */
export function formatarHoraEMinuto(carimbo: string): string {
  const data = interpretar(carimbo);
  if (data === null) return carimbo;
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false }).format(data);
}

/** `2026-09-30T23:59:00-03:00` -> `30/09/2026`. Para "ultima compra" e validade de cupom. */
export function formatarDataCompleta(carimbo: string): string {
  const data = interpretar(carimbo);
  if (data === null) return carimbo;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(data);
}

/**
 * §3.8 — `18/09` e lido "dezoito barra zero nove" pelo TalkBack. O rotulo diz
 * "18 de setembro".
 *
 * Devolve `null` quando nao ha carimbo interpretavel: quem chama entao **nao** passa
 * `aria-label`, e o leitor le o texto visivel. Um `aria-label` com o proprio texto visivel
 * dentro seria pior que nenhum.
 */
export function rotularDataParaLeitor(carimbo: string): string | null {
  const data = interpretar(carimbo);
  if (data === null) return null;
  return new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long' }).format(data);
}

/** §3.8 — `14:32` vira "14 horas e 32 minutos". */
export function rotularHoraParaLeitor(carimbo: string): string | null {
  const data = interpretar(carimbo);
  if (data === null) return null;
  const horas = data.getHours();
  const minutos = data.getMinutes();
  const parteHora = horas === 1 ? '1 hora' : `${String(horas)} horas`;
  if (minutos === 0) return parteHora;
  const parteMinuto = minutos === 1 ? '1 minuto' : `${String(minutos)} minutos`;
  return `${parteHora} e ${parteMinuto}`;
}

function interpretar(carimbo: string): Date | null {
  if (carimbo === '') return null;
  const milissegundos = Date.parse(carimbo);
  if (Number.isNaN(milissegundos)) return null;
  return new Date(milissegundos);
}
